import { Controller } from "@hotwired/stimulus";

// One native input owns editing, autofill, validation, and form submission.
export default class extends Controller {
  static targets = ["input", "cell"];

  connect() {
    this.element.classList.add("passcode-input--enhanced");
    this.normalize();
    // A closed dialog receives focus from its own controller after showModal().
    const dialog = this.element.closest("dialog");
    if (!dialog || dialog.open) this.inputTarget.focus({ preventScroll: true });
  }

  disconnect() {
    this.element.classList.remove("passcode-input--enhanced");
  }

  digits(value) {
    return value.replace(/[^0-9]/g, "");
  }

  normalize() {
    const input = this.inputTarget;
    const start = this.digits(input.value.slice(0, input.selectionStart)).length;
    const end = this.digits(input.value.slice(0, input.selectionEnd)).length;
    const value = this.digits(input.value).slice(0, this.cellTargets.length);
    if (input.value !== value) {
      input.value = value;
      input.setSelectionRange(Math.min(start, value.length), Math.min(end, value.length));
    }
    this.render();
  }

  paste(event) {
    if (!event.clipboardData) return;
    event.preventDefault();
    const input = this.inputTarget;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const available = this.cellTargets.length - (input.value.length - (end - start));
    const pasted = this.digits(event.clipboardData.getData("text")).slice(0, available);
    input.setRangeText(pasted, start, end, "end");
    input.dispatchEvent(new Event("input", { bubbles: true }));
  }

  placeCursor(event) {
    // Leave keyboard/screen-reader activation and native selection dragging alone.
    if (event.detail !== 1 || this.inputTarget.selectionStart !== this.inputTarget.selectionEnd) return;
    if (window.matchMedia("(forced-colors: active)").matches) return;
    const index = this.cellTargets.findIndex((cell) => {
      const bounds = cell.getBoundingClientRect();
      return event.clientX >= bounds.left && event.clientX <= bounds.right;
    });
    if (index < 0) return;
    const start = Math.min(index, this.inputTarget.value.length);
    const end = Math.min(start + 1, this.inputTarget.value.length);
    this.inputTarget.setSelectionRange(start, end);
    this.render();
  }

  render() {
    const input = this.inputTarget;
    const focused = document.activeElement === input;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    this.cellTargets.forEach((cell, index) => {
      cell.textContent = input.value[index] || "";
      cell.classList.toggle("is-current", focused && index === Math.min(start, this.cellTargets.length - 1));
      cell.classList.toggle("is-selected", focused && index >= start && index < end);
      cell.classList.toggle("has-caret", focused && start === end && index === start && !input.value[index]);
    });
  }
}
